import moment from 'moment';
import { DateFormats, DEFAULT_COUNTRY_CODE } from 'UI/constants/defaults';
import { localTimeFormatter } from 'UI/utils';

export const getInternalNumber = user => {
  if (!user?.personalInformation?.contact?.phone) return null;

  return `${DEFAULT_COUNTRY_CODE}${user?.personalInformation?.contact?.phone}`;
};

export const findExtremeItemsByDate = items => {
  if (!Array.isArray(items) || items.length === 0) {
    return { newest: null, oldest: null };
  }

  const hasInvalidDate = items.some(
    item => !item.date || Number.isNaN(new Date(item.date).getTime())
  );

  if (hasInvalidDate) {
    return { newest: null, oldest: null };
  }

  return items.reduce(
    (extremes, current) => {
      const currentDate = new Date(current.date);
      const newest = currentDate > new Date(extremes.newest.date) ? current : extremes.newest;
      const oldest = currentDate < new Date(extremes.oldest.date) ? current : extremes.oldest;

      return { newest, oldest };
    },
    { newest: items[0], oldest: items[0] }
  );
};

export const formatAttachmentsInResponse = responseMessages => {
  if (!responseMessages) return [];

  return responseMessages.map(item => ({
    ...item,
    attachment: item.attachments?.[0]
      ? { url: item.attachments[0], size: item?.attachmentSize }
      : null
  }));
};

export const getDateLabel = date => {
  const day = moment(date, DateFormats.SimpleDate);

  if (!day.isValid()) {
    return 'Invalid Date';
  }

  const now = moment().startOf('day');
  const yesterday = now.clone().subtract(1, 'days');

  if (day.isSame(now, 'day')) {
    return 'Today';
  }

  if (day.isSame(yesterday, 'day')) {
    return 'Yesterday';
  }

  return day.format(DateFormats.SimpleDate);
};

export const getDatePart = dateString => localTimeFormatter(dateString, DateFormats.SimpleDate);

export const groupMessagesByDate = messages => {
  if (!Array.isArray(messages)) return {};

  const sortedMessages = sortMessagesByDate(messages);

  const result = sortedMessages.reduce((acc, message) => {
    const day = getDatePart(message.date);
    if (!acc[day]) acc[day] = [];
    acc[day].push(message);
    return acc;
  }, {});

  return result;
};

export const sanitizeAndValidatePhone = phoneNumber => {
  if (!phoneNumber) return null;

  // Remove all non-digit characters, including +
  const cleanedPhone = phoneNumber.replace(/\D/g, '');

  // Ensure the phone number is 10-15 digits long
  const isValidPhone = cleanedPhone.length >= 8 && cleanedPhone.length <= 15;

  const validPhone =
    isValidPhone && phoneNumber.startsWith('+') ? `+${cleanedPhone}` : `+1${cleanedPhone}`;

  return isValidPhone ? validPhone : null;
};

export const sortMessagesByDate = messages => {
  if (!Array.isArray(messages)) return [];

  return [...messages].sort((a, b) => new Date(a.date) - new Date(b.date));
};

export const updateConversationList = ({ conversations, id, updates }) => {
  if (!id) return conversations;

  const conversationIndex = conversations.findIndex(conversation => conversation.id === id);
  if (conversationIndex === -1) return conversations; // No matching conversation, return original

  const updatedConversation = {
    ...conversations[conversationIndex],
    ...updates
  };

  return conversations.map((conversation, index) =>
    index === conversationIndex ? updatedConversation : conversation
  );
};

export const updateMessageStatusV2 = ({ messages, messageToUpdate, status, error }) => {
  const updatedMessages = [...messages];

  const index = updatedMessages.findIndex(
    message => message.messageId === messageToUpdate.messageId
  );

  if (index !== -1) {
    updatedMessages[index] = {
      ...updatedMessages[index],
      status,
      error
    };
  }

  return updatedMessages;
};

export const sortConversationsByDate = conversationsToSort =>
  conversationsToSort.sort((a, b) => new Date(b.date) - new Date(a.date));

export const getDraftMessageKey = id => `draftMesssage_${id}`;
export const getDraftAttachmentKey = id => `draftAttachment_${id}`;
export const getDraftMessage = id => {
  try {
    return localStorage.getItem(getDraftMessageKey(id)) ?? '';
  } catch (error) {
    console.error('Error getting draft message:', error);
    return '';
  }
};
export const getDraftAttachment = id => {
  try {
    const savedAttachment = localStorage.getItem(getDraftAttachmentKey(id)) ?? null;
    return savedAttachment ? JSON.parse(savedAttachment) : null;
  } catch (error) {
    console.error('Error getting draft message attachment:', error);
    return null;
  }
};
